import React, { useState, useEffect, useCallback } from 'react';
import { X, Search, Building2, CheckCircle, AlertCircle, Link2, RefreshCw, FileText } from 'lucide-react';
import { patientsApi } from '../../api/patients';
import type { PatientCompanySearchResult } from '../../types/patient';

interface LinkPatientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (patientId: string) => void;
}

export const LinkPatientModal: React.FC<LinkPatientModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchBy, setSearchBy] = useState<'document' | 'email' | 'phone' | 'any'>('any');
  const [results, setResults] = useState<PatientCompanySearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [medicalRecordNumbers, setMedicalRecordNumbers] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setResults([]);
      setError(null);
      setSuccessMessage(null);
      setMedicalRecordNumbers({});
    }
  }, [isOpen]);

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    setError(null);
    setResults([]);

    try {
      const data = await patientsApi.searchCompany({ query: searchQuery.trim(), searchBy });
      setResults(data);
      if (data.length === 0) {
        setError('No se encontraron pacientes con esos datos en otros centros');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al buscar pacientes');
    } finally {
      setIsSearching(false);
    }
  }, [searchQuery, searchBy]);

  const handleLink = async (patient: PatientCompanySearchResult) => {
    setIsLinking(true);
    setError(null);

    try {
      const mrn = medicalRecordNumbers[patient.id]?.trim() || undefined;
      const result = await patientsApi.linkToSite({ 
        patientId: patient.id,
        medicalRecordNumber: mrn 
      });
      
      const mrnMsg = result.medicalRecordNumber ? ` con Nº Historia ${result.medicalRecordNumber}` : '';
      setSuccessMessage(`Paciente vinculado correctamente a ${result.siteName}${mrnMsg}`);
      
      setTimeout(() => {
        onSuccess(patient.id);
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al vincular paciente');
    } finally {
      setIsLinking(false);
    }
  };

  const handleMrnChange = (patientId: string, value: string) => {
    setMedicalRecordNumbers(prev => ({ ...prev, [patientId]: value.toUpperCase() }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <div className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">Vincular paciente existente</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Search Form */}
        <div className="p-4 border-b bg-gray-50">
          <p className="text-sm text-gray-600 mb-3">
            Busca un paciente que ya existe en otro centro de la empresa para vincularlo a este centro.
          </p>
          <div className="flex gap-2">
            <select
              value={searchBy}
              onChange={(e) => setSearchBy(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="any">Cualquier campo</option>
              <option value="document">Documento</option>
              <option value="email">Email</option>
              <option value="phone">Teléfono</option>
            </select>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="DNI, email o teléfono..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              onClick={handleSearch}
              disabled={isSearching || !searchQuery.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSearching ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              Buscar
            </button>
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto p-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm mb-4">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {successMessage && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm mb-4">
              <CheckCircle className="h-4 w-4 flex-shrink-0" />
              {successMessage}
            </div>
          )}

          {results.length > 0 && (
            <div className="space-y-3">
              {results.map((patient) => (
                <div
                  key={patient.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
                >
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 truncate">{patient.fullName}</h3>
                      <p className="text-sm text-gray-500">
                        {patient.documentType}: {patient.documentNumber} · {patient.email}
                      </p>
                      <p className="text-sm text-gray-500">{patient.phonePrimary} · {patient.insuranceCompanyName}</p>

                      {/* Centros vinculados */}
                      <div className="mt-2 flex flex-wrap gap-1">
                        {patient.linkedSites.map((site) => (
                          <span
                            key={site.siteId}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${
                              site.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            <Building2 className="h-3 w-3" />
                            {site.siteName}
                            {site.medicalRecordNumber && (
                              <span className="text-gray-500">({site.medicalRecordNumber})</span>
                            )}
                          </span>
                        ))}
                      </div>

                      {/* Input de número de historia */}
                      {(!patient.isLinkedToCurrentSite || !patient.isActiveInCurrentSite) && (
                        <div className="mt-3 flex items-center gap-2">
                          <FileText className="h-4 w-4 text-gray-400" />
                          <input
                            type="text"
                            value={medicalRecordNumbers[patient.id] || ''}
                            onChange={(e) => handleMrnChange(patient.id, e.target.value)}
                            placeholder="Nº Historia (opcional)"
                            maxLength={50}
                            className="flex-1 max-w-48 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      )}
                    </div>

                    {/* Botón de acción */}
                    <div className="flex-shrink-0">
                      {patient.isLinkedToCurrentSite && patient.isActiveInCurrentSite ? (
                        <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-sm">
                          <CheckCircle className="h-4 w-4" />
                          Ya vinculado
                        </span>
                      ) : patient.isLinkedToCurrentSite && !patient.isActiveInCurrentSite ? (
                        <button
                          onClick={() => handleLink(patient)}
                          disabled={isLinking}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 disabled:opacity-50"
                        >
                          <RefreshCw className={`h-4 w-4 ${isLinking ? 'animate-spin' : ''}`} />
                          Reactivar
                        </button>
                      ) : (
                        <button
                          onClick={() => handleLink(patient)}
                          disabled={isLinking}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
                        >
                          <Link2 className={`h-4 w-4 ${isLinking ? 'animate-spin' : ''}`} />
                          Vincular
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!isSearching && results.length === 0 && !error && (
            <div className="text-center py-8 text-gray-500">
              <Search className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>Busca por documento, email o teléfono</p>
              <p className="text-sm">para encontrar pacientes de otros centros</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default LinkPatientModal;
